import React, {useState, useEffect} from 'react';
import {useParams, useNavigate} from 'react-router-dom';
import {
    Tabs,
    Card,
    Typography,
    Input,
    Button,
    Form,
    Space,
    Spin,
    App,
    Badge,
    Row,
    Col,
    Steps,
    Select,
    InputNumber,
    TimePicker,
    Checkbox,
    Divider,
    Radio, Popover, ColorPicker, AutoComplete, Empty
} from 'antd';
import {v4 as uuidv4} from 'uuid';
import dayjs from 'dayjs';
import {authService, cashRegisterService} from '../../services/api';
import {usePageTitle} from '../../context/PageTitleContext';
import ErrorPage from "../ErrorPage/ErrorPage";
import HorizontalFormItem from "../../components/HorizontalItems/FormItem";

// Функция для форматирования даты
const formatDate = (dateString) => {
    if (!dateString) return 'Не указано';
    const date = new Date(dateString);
    const day = date.getDate().toString().padStart(2, '0');
    const month = (date.getMonth() + 1).toString().padStart(2, '0');
    const year = date.getFullYear();
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${day}.${month}.${year} в ${hours}:${minutes}`;
};

const {Title, Text} = Typography;
const {TabPane} = Tabs;



// Компонент для создания времени продажи алкоголя
const AlcoholSaleTimeEditor = ({ value, onChange }) => {
    // Конвертация времени в секунды
    const timeToSeconds = (time) => {
        if (!time) return 0;
        const [hours, minutes] = time.split(':').map(Number);
        return hours * 3600 + minutes * 60;
    };

    // Конвертация секунд во время
    const secondsToTime = (seconds) => {
        if (seconds === undefined || seconds === null) return '00:00';
        const hours = Math.floor(seconds / 3600);
        const mins = Math.floor((seconds % 3600) / 60);
        return `${hours.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}`;
    };

    // Парсинг входящих данных
    const parseValue = (val) => {
        if (!val) return [];
        if (typeof val === 'string') {
            try {
                return JSON.parse(val);
            } catch (e) {
                return [];
            }
        }
        if (Array.isArray(val)) {
            return val;
        }
        return [];
    };

    const [timeSlots, setTimeSlots] = useState(() => {
        const parsed = parseValue(value);
        if (!Array.isArray(parsed) || parsed.length === 0) {
            return [{ id: uuidv4(), from: '10:00', to: '22:00' }];
        }

        return parsed.map(slot => ({
            id: uuidv4(),
            from: secondsToTime(slot.from),
            to: secondsToTime(slot.to)
        }));
    });

    // Проверка пересечения времени
    const hasTimeConflict = (newSlot, currentIndex) => {
        const currentFrom = timeToSeconds(newSlot.from);
        const currentTo = timeToSeconds(newSlot.to);

        return timeSlots.some((slot, index) => {
            if (index === currentIndex) return false;

            const slotFrom = timeToSeconds(slot.from);
            const slotTo = timeToSeconds(slot.to);

            return currentFrom < slotTo && currentTo > slotFrom;
        });
    };

    const updateTimeSlots = (newTimeSlots) => {
        setTimeSlots(newTimeSlots);

        // Конвертация в формат API
        const apiFormat = newTimeSlots.map(slot => ({
            from: timeToSeconds(slot.from),
            to: timeToSeconds(slot.to)
        }));

        onChange(apiFormat);
    };

    const handleTimeSlotChange = (index, field, value) => {
        const newTimeSlots = [...timeSlots];
        newTimeSlots[index] = { ...newTimeSlots[index], [field]: value };

        updateTimeSlots(newTimeSlots);
    };

    const removeTimeSlot = (index) => {
        if (timeSlots.length > 1) {
            const newTimeSlots = timeSlots.filter((_, i) => i !== index);
            updateTimeSlots(newTimeSlots);
        }
    };

    return (
        <div>
            {timeSlots.map((slot, index) => {
                const hasConflict = hasTimeConflict(slot, index);

                return (
                    <div
                        key={slot.id}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginBottom: 12,
                            gap: 12,
                            padding: hasConflict ? '8px' : '0',
                            backgroundColor: hasConflict ? '#fff2f0' : 'transparent',
                            borderRadius: 6,
                        }}
                    >
                        <TimePicker
                            format="HH:mm"
                            value={slot.from ? dayjs(slot.from, 'HH:mm') : null}
                            onChange={(time) => handleTimeSlotChange(index, 'from', time ? time.format('HH:mm') : '00:00')}
                            placeholder="10:00"
                            style={{ width: 100 }}
                        />

                        <span>—</span>

                        <TimePicker
                            format="HH:mm"
                            value={slot.to ? dayjs(slot.to, 'HH:mm') : null}
                            onChange={(time) => handleTimeSlotChange(index, 'to', time ? time.format('HH:mm') : '23:59')}
                            placeholder="22:00"
                            style={{ width: 100 }}
                        />

                        {timeSlots.length > 1 && (
                            <Button
                                type="text"
                                danger
                                size="small"
                                onClick={() => removeTimeSlot(index)}
                                style={{ minWidth: 'auto', padding: '4px 8px' }}
                            >
                                ✕
                            </Button>
                        )}
                    </div>
                );
            })}
        </div>
    );
};

const QuickProductsEditor = ({ value = [], onChange }) => {
    const [groups, setGroups] = useState(() => {
        if (!Array.isArray(value) || value.length === 0) {
            return [{ group_name: 'Основная группа', products: Array(30).fill(null) }];
        }
        return value.map(group => ({
            ...group,
            products: [...(group.products || []), ...Array(30 - (group.products?.length || 0)).fill(null)]
        }));
    });

    const [activePopover, setActivePopover] = useState(null);
    const [searchText, setSearchText] = useState('');
    const [searchResults, setSearchResults] = useState([]);
    const [searchLoading, setSearchLoading] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);

    const colors = [
        { name: 'lightgray', rgb: 'rgb(211, 211, 211)' },
        { name: 'lightcoral', rgb: 'rgb(255, 178, 169)' },
        { name: 'peachpuff', rgb: 'rgb(255, 193, 125)' },
        { name: 'khaki', rgb: 'rgb(253, 212, 129)' },
        { name: 'lightgreen', rgb: 'rgb(135, 227, 154)' },
        { name: 'lightskyblue', rgb: 'rgb(143, 205, 255)' },
        { name: 'cornflowerblue', rgb: 'rgb(159, 184, 255)' },
        { name: 'plum', rgb: 'rgb(222, 163, 236)' }
    ];

    const getColorRgb = (colorName) => {
        const color = colors.find(c => c.name === colorName);
        return color ? color.rgb : 'rgb(211, 211, 211)';
    };

    const updateGroups = (newGroups) => {
        setGroups(newGroups);
        const filteredGroups = newGroups.map(group => ({
            ...group,
            products: group.products.filter(p => p !== null)
        }));
        onChange(filteredGroups);
    };

    const searchProducts = async (name) => {
        if (!name || name.length < 2) {
            setSearchResults([]);
            return;
        }

        setSearchLoading(true);
        try {
            const response = await cashRegisterService.getProductsByName(name);
            if (response.status === 'success') {
                setSearchResults(response.data.items || []);
            }
        } catch (error) {
            console.error('Ошибка поиска товаров:', error);
        } finally {
            setSearchLoading(false);
        }
    };

    const handleProductSelect = (groupIndex, productIndex, product) => {
        const newGroups = [...groups];
        newGroups[groupIndex].products[productIndex] = {
            id: product.id,
            name: product.name.length > 50 ? product.name.substring(0, 50) : product.name,
            color: 'lightgray'
        };
        updateGroups(newGroups);
        setActivePopover(null);
        setSearchText('');
        setSearchResults([]);
    };

    const handleProductEdit = (groupIndex, productIndex, field, newValue) => {
        const newGroups = [...groups];
        if (newGroups[groupIndex].products[productIndex]) {
            newGroups[groupIndex].products[productIndex][field] = newValue;
            updateGroups(newGroups);
        }
    };

    const handleProductDelete = (groupIndex, productIndex) => {
        const newGroups = [...groups];
        newGroups[groupIndex].products[productIndex] = null;
        updateGroups(newGroups);
        setActivePopover(null);
    };

    const addGroup = () => {
        const newGroupName = `Группа ${groups.length + 1}`;
        const newGroups = [...groups, {
            group_name: newGroupName,
            products: Array(30).fill(null)
        }];
        updateGroups(newGroups);
    };

    const updateGroupName = (groupIndex, newName) => {
        const newGroups = [...groups];
        newGroups[groupIndex].group_name = newName;
        updateGroups(newGroups);
    };

    const deleteGroup = (groupIndex) => {
        if (groups.length > 1) {
            const newGroups = groups.filter((_, i) => i !== groupIndex);
            updateGroups(newGroups);
        }
    };

    const renderEmptyProductPopover = (groupIndex, productIndex) => (
        <div style={{ width: 300, padding: 8 }}>
            <AutoComplete
                style={{ width: '100%', marginBottom: 8 }}
                placeholder="Поиск товара..."
                value={searchText}
                options={searchResults.map(item => ({
                    value: item.name,
                    label: item.name,
                    key: item.id
                }))}
                onSearch={(text) => {
                    setSearchText(text);
                    searchProducts(text);
                }}
                onSelect={(value, option) => {
                    const product = searchResults.find(p => p.id === option.key);
                    if (product) {
                        handleProductSelect(groupIndex, productIndex, product);
                    }
                }}
                notFoundContent={searchLoading ? <Spin size="small" /> : <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Товары не найдены" />}
            />
        </div>
    );

    const renderFilledProductPopover = (groupIndex, productIndex, product) => (
        <div style={{ width: 250, padding: 8 }}>
            <div style={{ marginBottom: 12 }}>
                <Text strong>Название:</Text>
                <Input
                    value={product.name}
                    onChange={(e) => handleProductEdit(groupIndex, productIndex, 'name', e.target.value)}
                    style={{ marginTop: 4 }}
                    maxLength={50}
                />
            </div>
            <div style={{ marginBottom: 12 }}>
                <Text strong>Цвет:</Text>
                <div style={{ display: 'flex', gap: 4, marginTop: 4, flexWrap: 'wrap' }}>
                    {colors.map(color => (
                        <div
                            key={color.name}
                            onClick={() => handleProductEdit(groupIndex, productIndex, 'color', color.name)}
                            style={{
                                width: 24,
                                height: 24,
                                backgroundColor: color.rgb,
                                cursor: 'pointer',
                                border: product.color === color.name ? '2px solid #1890ff' : '1px solid #d9d9d9',
                                borderRadius: 4
                            }}
                        />
                    ))}
                </div>
            </div>
            <Button
                danger
                size="small"
                onClick={() => handleProductDelete(groupIndex, productIndex)}
                style={{ width: '100%' }}
            >
                Удалить товар
            </Button>
        </div>
    );

    const renderProductTile = (groupIndex, productIndex, product) => {
        const popoverId = `${groupIndex}-${productIndex}`;
        const isActive = activePopover === popoverId;

        return (
            <Popover
                key={productIndex}
                content={product ?
                    renderFilledProductPopover(groupIndex, productIndex, product) :
                    renderEmptyProductPopover(groupIndex, productIndex)
                }
                trigger="click"
                open={isActive}
                onOpenChange={(open) => {
                    setActivePopover(open ? popoverId : null);
                    if (!open) {
                        setSearchText('');
                        setSearchResults([]);
                    }
                }}
            >
                <div
                    style={{
                        width: 120,
                        height: 120,
                        border: '1px solid #d9d9d9',
                        borderRadius: 6,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        backgroundColor: product ? getColorRgb(product.color) : '#fafafa',
                        fontSize: 12,
                        textAlign: 'center',
                        padding: 4,
                        transition: 'all 0.2s',
                        ':hover': {
                            borderColor: '#1890ff'
                        }
                    }}
                >
                    {product ? (
                        <Text style={{ color: '#333', fontWeight: 500 }}>
                            {product.name}
                        </Text>
                    ) : (
                        <Text type="secondary">+</Text>
                    )}
                </div>
            </Popover>
        );
    };

    return (
        <div>
            <div style={{ marginBottom: 16 }}>
                <Button type="dashed" onClick={addGroup} style={{ width: '100%' }}>
                    + Добавить группу
                </Button>
            </div>

            {groups.map((group, groupIndex) => (
                <Card
                    key={groupIndex}
                    title={
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Input
                                value={group.group_name}
                                onChange={(e) => updateGroupName(groupIndex, e.target.value)}
                                style={{ flex: 1 }}
                                bordered={false}
                            />
                            {groups.length > 1 && (
                                <Button
                                    type="text"
                                    danger
                                    size="small"
                                    onClick={() => deleteGroup(groupIndex)}
                                >
                                    ✕
                                </Button>
                            )}
                        </div>
                    }
                    style={{ marginBottom: 16 }}
                >
                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(5, 1fr)',
                        gap: 8
                    }}>
                        {group.products.map((product, productIndex) =>
                            renderProductTile(groupIndex, productIndex, product)
                        )}
                    </div>
                </Card>
            ))}
        </div>
    );
};

const CashRegisterEdit = () => {
    const {id} = useParams();
    const navigate = useNavigate();
    const {message} = App.useApp();
    const {setPageTitle, setRightContent} = usePageTitle();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [cashRegister, setCashRegister] = useState(null);
    const [saving, setSaving] = useState(false);
    const [correlationKey, setCorrelationKey] = useState(null);
    const [idempotencyKey, setIdempotencyKey] = useState(null);
    const [formData, setFormData] = useState({
        payment_methods_list: [],
        fiscal_cash_registers: [],
        receipt_width: [],
        tax_rate: []
    });

    // Состояние для отслеживания предыдущего тела запроса
    const [previousBodyString, setPreviousBodyString] = useState('');

    useEffect(() => {
        fetchData();
        setPageTitle('Редактирование кассы');

        setRightContent(
            authService.hasPermission('pos:cash_registers:edit') && (<Button
                type="primary"
                onClick={handleSave}
                loading={saving}
            >
                Сохранить
            </Button>)
        );

        return () => {
            setPageTitle('');
            setRightContent(null);
            setCashRegister(null);
        };
    }, [setPageTitle, setRightContent, id]);

    useEffect(() => {
        if (cashRegister) {
            // Подготавливаем данные для формы
            const formValues = {
                ...cashRegister,
                payment_methods: cashRegister.payment_methods?.map(pm => pm.id) || [],
                fiscal_cash_id: cashRegister.fiscal_cash?.id || null
            };

            form.setFieldsValue(formValues);
        }
    }, [cashRegister, form]);

    const fetchData = async () => {
        try {
            setLoading(true);

            // Получаем данные кассы
            const cashRegisterResponse = await cashRegisterService.getCashRegisterById(id);

            // Получаем данные для формы
            const formDataResponse = await cashRegisterService.getFormData();

            if (cashRegisterResponse.status === 'success' && formDataResponse.status === 'success') {
                setCashRegister(cashRegisterResponse.data);
                setFormData(formDataResponse.data);
                console.log('Получены данные кассы:', cashRegisterResponse.data);
            } else {
                message.error('Не удалось загрузить данные кассы');
                navigate('/cash-registers');
            }
        } catch (error) {
            if (error.response?.status === 404) {
                setError({
                    statusCode: 404,
                    title: "Касса не найдена",
                    description: "Указанная касса не существует или была удалена."
                });
            } else {
                setError({
                    statusCode: error.response?.status || 500,
                    title: "Ошибка загрузки данных",
                    description: "Произошла ошибка при загрузке данных кассы. Попробуйте обновить страницу или вернуться позже."
                });
            }
        } finally {
            setLoading(false);
        }
    };

    // Обработка сохранения данных кассы
    const handleSave = async () => {
        try {
            const values = await form.validateFields();

            // Получаем данные пользователя из localStorage
            const userData = JSON.parse(localStorage.getItem('user') || '{}');
            const username = userData.username || '';
            const firstName = userData.first_name || '';
            const lastName = userData.last_name || '';
            const fullName = `${firstName} ${lastName}`.trim();

            // Преобразование данных перед отправкой
            const formattedValues = {
                ...values,
                username: username,
                full_name: fullName
            };

            console.log('Сохраняемые данные:', formattedValues);

            // Генерируем или обновляем ключ идемпотентности
            const currentBodyString = JSON.stringify(formattedValues);
            let currentIdempotencyKey = idempotencyKey;
            if (!currentIdempotencyKey || previousBodyString !== currentBodyString) {
                currentIdempotencyKey = uuidv4();
                setIdempotencyKey(currentIdempotencyKey);
                setPreviousBodyString(currentBodyString);
            }

            // Всегда генерируем новый ключ корреляции
            const newCorrelationKey = uuidv4();
            setCorrelationKey(newCorrelationKey);

            setSaving(true);

            // Передаем оба ключа в API
            const response = await cashRegisterService.saveCashRegister(
                id,
                formattedValues,
                newCorrelationKey,
                currentIdempotencyKey
            );

            if (response.status === 'success') {
                message.success('Касса успешно сохранена');
                fetchData(); // Обновляем данные после сохранения
            } else {
                message.error(response.message || 'Ошибка при сохранении кассы');
            }
        } catch (error) {
            console.error('Ошибка при сохранении:', error);
            message.error(error.message || 'Ошибка при сохранении кассы');
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{textAlign: 'center', padding: '50px 0'}}>
                <Spin size="large"/>
            </div>
        );
    }

    if (error) {
        return <ErrorPage
            statusCode={error.statusCode}
            title={error.title}
            description={error.description}
        />;
    }

    return (
        <Card>
            <Title level={3} style={{marginTop: 0}}>
                Касса «{cashRegister.name}»
            </Title>
            <div style={{marginBottom: 24}}>
                <Text>ID: <Text strong>{cashRegister.id}</Text></Text>
            </div>

            <Form
                form={form}
                layout="horizontal"
                initialValues={cashRegister}
            >
                <Row gutter={24}>
                    <Col span={12}>
                        <Card title="Основные атрибуты" style={{marginBottom: 24}}>
                            <HorizontalFormItem
                                label="Название"
                                name="name"
                                rules={[
                                    {required: true, message: 'Введите название кассы'},
                                    {max: 255, message: 'Максимальная длина - 255 символов'}
                                ]}
                            >
                                <Input placeholder="Введите название кассы" maxLength={255}/>
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Внешний ID"
                                name="external_id"
                                rules={[
                                    {required: true, message: 'Введите внешний ID'}
                                ]}
                            >
                                <Input placeholder="Введите внешний ID"/>
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="НДС"
                                name="tax_rate"
                            >
                                <Select
                                    options={[
                                        { label: 'Без НДС', value: null },
                                        ...formData.tax_rate.map(rate => ({
                                            label: rate.label,
                                            value: rate.value
                                        }))
                                    ]}
                                    placeholder="Выберите НДС"
                                    allowClear
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Ширина чека"
                                name="receipt_width"
                                rules={[
                                    {required: true, message: 'Выберите ширину чека'}
                                ]}
                            >
                                <Select
                                    options={formData.receipt_width.map(width => ({
                                        label: width.label,
                                        value: width.value
                                    }))}
                                    placeholder="Выберите ширину чека"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Виртуальная касса"
                                name="is_virtual"
                                rules={[
                                    {required: true, message: 'Выберите тип кассы'}
                                ]}
                            >
                                <Radio.Group>
                                    <Radio.Button value="yes">Да</Radio.Button>
                                    <Radio.Button value="no">Нет</Radio.Button>
                                </Radio.Group>
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Принтер чеков"
                                name="receipt_printer_name"
                            >
                                <Input placeholder="Введите название принтера"/>
                            </HorizontalFormItem>
                        </Card>

                        <Card title="Способы оплаты" style={{marginBottom: 24}}>
                            <HorizontalFormItem
                                label="Доступные способы"
                                name="payment_methods"
                                rules={[
                                    {required: true, message: 'Выберите способы оплаты'}
                                ]}
                            >
                                <Select
                                    mode="multiple"
                                    options={formData.payment_methods_list.map(method => ({
                                        label: method.label || method.name,
                                        value: method.value || method.id
                                    }))}
                                    placeholder="Выберите способы оплаты"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </HorizontalFormItem>

                            <HorizontalFormItem
                                label="Процент кэшбека бонусной системы"
                                name="customer_bonus_percent"
                                rules={[
                                    { required: true, message: 'Процент кэшбека бонусной системы' }
                                ]}
                            >
                                <InputNumber
                                    placeholder="Введите процент кэшбека бонусной системы"
                                    style={{ width: '100%' }}
                                    precision={2}
                                    min={0}
                                    max={999999999999.99} // 12 цифр до запятой и 2 после
                                    step={0.01}
                                    stringMode
                                />
                            </HorizontalFormItem>
                        </Card>
                    </Col>
                    <Col span={12}>
                        <Card title="Фискальные данные" style={{marginBottom: 24}}>
                            <HorizontalFormItem
                                label="Фискальная касса"
                                name="fiscal_cash_id"
                                rules={[
                                    {required: true, message: 'Выберите фискальную кассу'}
                                ]}
                            >
                                <Select
                                    options={formData.fiscal_cash_registers.map(fiscal => ({
                                        label: fiscal.label || fiscal.name,
                                        value: fiscal.value || fiscal.id
                                    }))}
                                    placeholder="Выберите фискальную кассу"
                                    showSearch
                                    filterOption={(input, option) =>
                                        (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                                    }
                                />
                            </HorizontalFormItem>
                        </Card>

                        <Card title="Время продажи алкоголя" style={{marginBottom: 24}}>
                            <HorizontalFormItem
                                label="Разрешенное время"
                                name="alcohol_sale_time"
                                rules={[
                                    {
                                        validator: (_, value) => {
                                            if (!value || value.length === 0) {
                                                return Promise.resolve(); // Поле не обязательное
                                            }

                                            return Promise.resolve();
                                        }
                                    }
                                ]}
                            >
                                <AlcoholSaleTimeEditor />
                            </HorizontalFormItem>
                        </Card>
                    </Col>
                    <Col span={24}>
                        <Card title="Быстрые товары" style={{marginBottom: 24}}>
                            <HorizontalFormItem
                                label="Группы товаров"
                                name="quick_products"
                            >
                                <QuickProductsEditor />
                            </HorizontalFormItem>
                        </Card>
                    </Col>
                </Row>
            </Form>
        </Card>
    );
}

export default CashRegisterEdit;